import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'line',
  name_en: 'Line',
  name_zh: '折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE,
      priority: DEFINE.DATE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: [DEFINE.ARRAY, DEFINE.SINGLE]
    },
    z: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE,
      priority: DEFINE.STR,
      optional: true
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string[];
    z?: string;
  };
  /** 是否堆叠 */
  dvStack?: boolean;
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: false,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const {
      dataIndex = 0,
      encoding: { x, y: yTemp, z },
      dvStack = false
    } = spec;
    let y = Array.isArray(yTemp) ? yTemp : [yTemp];
    // 折线图不支持多Y轴与第三维度指标（z）同时存在，若是同时存在，则将多y轴降为单Y轴（多Y轴包含了第三维度指标）
    if (y.length >= 2 && typeof z !== 'undefined') {
      y = [y[0]];
    }

    // * data ------------------------------------------------------------
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const { x: xAxisData, z: zNameData = y } = util.dataEncoding.encoding(data.values.slice(), {
      x: {
        field: x,
        transforms: [{ type: 'sort', order: 'ascending' }, { type: 'deDuplication' }]
      },
      z: {
        field: z!,
        transforms: [
          { type: 'deDuplication' },
          { type: 'filter', cb: val => typeof val !== 'undefined' }
        ]
      }
    });

    // * chart ------------------------------------------------------------
    const chartViewRect = $dom.getBoundingClientRect();
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption
    };
    const series = zNameData.map((zItem, index) => {
      return {
        type: 'line',
        name: zItem,
        stack: dvStack ? 'stack-line' : undefined,
        // TODO:Insight需要必须包含xAxisIndex和yAxisIndex
        xAxisIndex: 0,
        yAxisIndex: 0,
        __encodeField: zItem,
        data: util.dataTransform.transform(
          xAxisData.map(
            xItem =>
              data.values.find(
                dataItem =>
                  dataItem[x] === xItem && (typeof z === 'undefined' || dataItem[z] === zItem)
                // 非多Y轴下，取0位置值
              )?.[y[y.length >= 2 ? index : 0]]
          ) as number[],
          [{ type: 'toNumeric' }]
        )
      };
    });

    const yAxis = [y[0]].map((_, index) => {
      return {
        show: true,
        __dataIndex: index,
        __encodeField: y,
        type: 'value',
        name: y[index] + (dataMetaInfo[y[index]]?.unit ? `(${dataMetaInfo[y[index]].unit})` : ''),
        dvSplitLineNumber: hook.getSplitLineNumber(
          chartViewRect.height - (grid.top || 0) - grid.bottom
        )
      };
    });

    return {
      grid: [grid],
      tooltip: {
        trigger: 'axis'
      },
      xAxis: [
        {
          show: true,
          __dataIndex: dataIndex,
          __encodeField: x,
          type: 'category',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData as string[],
          boundaryGap: false,
          axisTick: {
            show: false
          }
        }
      ],
      yAxis,
      series
    };
  }
});
